import { useMemo, useState } from "react";
import { Team } from "../graph";

type Props = {
  teams: Team[];
  selected: string[];
  onChange: (ids: string[]) => void;
};

type SortKey = "displayName" | "createdDateTime" | "lastActivityDateTime";
type SortDir = "asc" | "desc";

function fmt(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return iso;
  }
}

export default function TeamSelector({ teams, selected, onChange }: Props) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("displayName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = needle
      ? teams.filter(
          (t) =>
            t.displayName.toLowerCase().includes(needle) ||
            (t.description?.toLowerCase().includes(needle) ?? false)
        )
      : teams;

    const sorted = [...base].sort((a, b) => {
      const av = (a[sortKey] ?? "") as string;
      const bv = (b[sortKey] ?? "") as string;
      const cmp = av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [teams, q, sortKey, sortDir]);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const visibleIds = filtered.map((t) => t.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      onChange(selected.filter((id) => !visibleIds.includes(id)));
    } else {
      onChange(Array.from(new Set([...selected, ...visibleIds])));
    }
  };

  const setSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const arrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  if (teams.length === 0) {
    return <p className="muted">No Teams found for your account.</p>;
  }

  return (
    <div>
      <div className="row" style={{ marginBottom: 12, gap: 12 }}>
        <input
          type="text"
          placeholder={`Search ${teams.length} Team${teams.length === 1 ? "" : "s"}…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <span className="muted">
          {filtered.length} shown · {selected.length} selected
        </span>
      </div>

      <div className="team-table-wrap">
        <table className="team-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  title="Select all visible"
                />
              </th>
              <th onClick={() => setSort("displayName")}>
                Team{arrow("displayName")}
              </th>
              <th onClick={() => setSort("createdDateTime")} style={{ width: 130 }}>
                Created{arrow("createdDateTime")}
              </th>
              <th onClick={() => setSort("lastActivityDateTime")} style={{ width: 150 }}>
                Last activity{arrow("lastActivityDateTime")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr
                key={t.id}
                className={selected.includes(t.id) ? "selected" : ""}
                onClick={() => toggle(t.id)}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                </td>
                <td>
                  <div>{t.displayName}</div>
                  {t.description && <div className="muted small">{t.description}</div>}
                </td>
                <td>{fmt(t.createdDateTime)}</td>
                <td>{fmt(t.lastActivityDateTime)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="muted" style={{ textAlign: "center" }}>
                  No matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
